import { Grid, MenuItem, Select } from '@mui/material';
import { Checkbox, HSpacer, RoutalDialog, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';

import { withStyles } from '@mui/styles';
import { useEffect, useState } from 'react';
import Trash from '../../../../../assets/svg/onboarding/icons/Trash';
import { Project } from '../../../../../domain/backoffice/project/Project';
import { ProjectCustomerWhatsAppType } from '../../../../../domain/project/projectCustomerWhatsApp/ProjectCustomerWhatsAppItem';
import { useUpdateProjectWhatsAppTemplate } from '../../../../../hooks/backoffice/projects/useUpdateProjectWhatsAppTemplate';
import Button from '../../../../../react-components/Button';
import InputText from '../../../../../react-components/InputText';
import Text from '../../../../../react-components/Text';

const styles = () => ({
  select: {
    height: `40px`,
    fontSize: `14px`,
    '&>div': {
      maxHeight: `40px`,
      padding: `10px`,
      backgroundColor: `transparent !important`,
      display: `flex`,
      flexDirection: `row` as `row`,
      justifyContent: `space-between`,
      alignItems: `center`,
    },
  },
});

const recordToArray = (object: Record<string, string>): Array<{ key: string; value: string }> => {
  return (
    Object.keys(object).map((key) => ({
      key,
      value: object[key] as string,
    })) ?? []
  );
};

const arrayToRecord = (array: Array<{ key: string; value: string }>): Record<string, string> => {
  return array.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.key]: curr.value,
    }),
    {}
  );
};

type BackofficeProjectsManageWhatsAppTemplateDialogProps = {
  classes: {
    select: string;
  };
  openDialog: boolean;
  project: Project;
  onClose: (hasBeenUpdated: boolean) => void;
};

export const BackofficeProjectsManageWhatsAppTemplateDialog = withStyles(styles)(
  ({ classes, openDialog, project, onClose }: BackofficeProjectsManageWhatsAppTemplateDialogProps) => {
    const projectCustomerWhatsApps = project.customer_whatsapp;
    const initType =
      projectCustomerWhatsApps.getCustomerAvailableWhatsAppTypes(project.view).length > 0
        ? projectCustomerWhatsApps.getCustomerAvailableWhatsAppTypes(project.view)[0]
        : `route_started`;

    const { run: updateWhatsAppTemplate, isLoading, success } = useUpdateProjectWhatsAppTemplate();

    const [count, setCount] = useState<number>(1);
    const [id, setId] = useState<ProjectCustomerWhatsAppType>(initType);
    const [subaccountId, setSubaccountId] = useState<string>(
      projectCustomerWhatsApps.getTemplateByType(initType)?.subaccount_id ?? ``
    );
    const [from, setFrom] = useState<string>(projectCustomerWhatsApps.getTemplateByType(initType)?.from ?? ``);
    const [enabled, setEnabled] = useState<boolean>(
      projectCustomerWhatsApps.getTemplateByType(initType)?.enabled ?? false
    );
    const [templateId, setTemplateId] = useState<string>(
      projectCustomerWhatsApps.getTemplateByType(initType)?.template_id ?? ``
    );
    const [replacement_values, setReplacementValues] = useState<Array<{ key: string; value: string }>>(
      recordToArray(projectCustomerWhatsApps.getTemplateByType(initType)?.replacement_values ?? {})
    );

    useEffect(() => {
      if (success) {
        onClose(true);
      }
    }, [onClose, success]);

    useEffect(() => {
      updateValues(id);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    if (!openDialog) return null;

    const isSaveButtonEnabled = () => {
      if (isLoading || !isDirtyState()) {
        return false;
      }

      return true;
    };

    const isDirtyState = () => {
      const template = projectCustomerWhatsApps.getTemplateByType(id);
      if (template) {
        if (
          (template.subaccount_id ?? ``) !== subaccountId ||
          (template.from ?? ``) !== from ||
          (template.enabled ?? false) !== enabled ||
          (template.template_id ?? ``) !== templateId ||
          JSON.stringify(recordToArray(template.replacement_values ?? {})) !== JSON.stringify(replacement_values)
        ) {
          return true;
        }
      }
      return false;
    };

    const updateValues = (id: ProjectCustomerWhatsAppType) => {
      const template = projectCustomerWhatsApps.getTemplateByType(id);
      if (template) {
        setSubaccountId(template.subaccount_id ?? ``);
        setFrom(template.from ?? ``);
        setEnabled(template.enabled ?? false);
        setTemplateId(template.template_id ?? ``);
        setReplacementValues(recordToArray(template.replacement_values ?? {}));
      }
    };

    return (
      <RoutalDialog
        open={openDialog}
        onCloseDialog={() => {
          if (!isLoading) {
            onClose(false);
          }
        }}
        dialogProps={{
          disableBackdropClick: true,
          disableEscapeKeyDown: true,
        }}
        titleContent={<Text variant="h6" intlId="backoffice.projects.whatsapp_template.title" />}
        footerContent={
          <Grid container direction="row" alignItems="center" justifyContent="flex-end" wrap="nowrap">
            <Button
              disabled={!isSaveButtonEnabled()}
              onClick={() => {
                const template = projectCustomerWhatsApps.getTemplateByType(id);
                if (!template) {
                  updateWhatsAppTemplate({
                    projectId: project.id,
                    id,
                    subaccountId,
                    from,
                    enabled,
                    templateId,
                    replacement_values: arrayToRecord(replacement_values),
                  });
                } else {
                  updateWhatsAppTemplate({
                    projectId: project.id,
                    id,
                    ...((template.subaccount_id ?? ``) !== subaccountId
                      ? { subaccountId: subaccountId === `` ? null : subaccountId }
                      : {}),
                    ...((template.from ?? ``) !== from ? { from: from === `` ? null : from } : {}),
                    ...(template.enabled !== enabled ? { enabled } : {}),
                    ...((template.template_id ?? ``) !== templateId ? { templateId } : {}),
                    ...(JSON.stringify(recordToArray(template.replacement_values ?? {})) !==
                    JSON.stringify(replacement_values)
                      ? { replacement_values: arrayToRecord(replacement_values) }
                      : {}),
                  });
                }
              }}
            >
              {isLoading ? (
                <Text style={{ color: `white` }} intlId="loading" />
              ) : (
                <Text
                  style={{ cursor: isSaveButtonEnabled() ? `pointer` : `default`, color: `white` }}
                  intlId="confirm"
                />
              )}
            </Button>
          </Grid>
        }
      >
        <Grid container direction="column" wrap="nowrap">
          <Select
            labelId="select-template-type"
            className={classes.select}
            id="select-template-type"
            value={id}
            onChange={(event) => {
              setId(event.target.value as ProjectCustomerWhatsAppType);
            }}
          >
            {projectCustomerWhatsApps.getCustomerAvailableWhatsAppTypes(project.view).map((type) => {
              const typeEnabled =
                (type !== id && projectCustomerWhatsApps.getTemplateByType(type)?.enabled) || (type === id && enabled);
              return (
                <MenuItem
                  key={type}
                  value={type}
                  style={{ display: `flex`, flexDirection: `row`, justifyContent: `space-between` }}
                >
                  <Text>{type}</Text>
                  {typeEnabled ? (
                    <Text style={{ color: RoutalPalette.success.medium }} intlId="enabled" />
                  ) : (
                    <Text style={{ color: RoutalPalette.error.medium }} intlId="disabled" />
                  )}
                </MenuItem>
              );
            })}
          </Select>

          <VSpacer medium />

          <Grid>
            <Text variant="label" intlId="backoffice.projects.whatsapp_template.enabled" />
            <Checkbox
              checked={enabled}
              onChange={(e: any, value: any) => {
                setEnabled(value);
              }}
            />
          </Grid>

          <VSpacer medium />

          <Grid>
            <Text variant="label" intlId="backoffice.projects.whatsapp_template.subaccount_id" />
            <InputText onChange={setSubaccountId} fieldValue={subaccountId} placeholder={`AC123123123`} fullWidth />
          </Grid>

          <VSpacer medium />

          <Grid>
            <Text variant="label" intlId="backoffice.projects.whatsapp_template.from" />
            <InputText onChange={setFrom} fieldValue={from} placeholder={`+34666666666`} fullWidth />
          </Grid>

          <VSpacer medium />

          <Grid>
            <Text variant="label" intlId="backoffice.projects.whatsapp_template.template_id" />
            <InputText onChange={setTemplateId} fieldValue={templateId} placeholder={`HX123412341234`} fullWidth />
          </Grid>

          <VSpacer medium />

          <Grid>
            <Text variant="label" intlId="backoffice.projects.whatsapp_template.replacements" />
            {replacement_values.map(({ key, value }, index) => (
              <Grid display={`flex`} flexDirection={`row`} flexGrow={1} marginTop={1}>
                <InputText
                  inputContainerStyles={{ width: `100%` }}
                  onChange={(newKey) => {
                    const newElement = { ...replacement_values[index], key: newKey };
                    setReplacementValues((prevValue) => {
                      prevValue[index] = newElement;
                      return [...prevValue];
                    });
                  }}
                  fieldValue={key}
                  placeholder={`1`}
                />
                <HSpacer small />
                <InputText
                  inputContainerStyles={{ width: `100%` }}
                  onChange={(newValue) => {
                    const newElement = { ...replacement_values[index], value: newValue };
                    setReplacementValues((prevValue) => {
                      prevValue[index] = newElement;
                      return [...prevValue];
                    });
                  }}
                  fieldValue={value}
                  placeholder={`stop_field`}
                />
                <HSpacer medium />
                <Button
                  variant={`text`}
                  style={{ padding: 0, border: `transparent` }}
                  onClick={() => {
                    setReplacementValues((prevValue) => [...prevValue.slice(0, index), ...prevValue.slice(index + 1)]);
                  }}
                >
                  <Trash color={RoutalPalette.error.medium} />
                </Button>
              </Grid>
            ))}

            <Grid display={`flex`} justifyContent={`center`} marginTop={2}>
              <Button
                variant={`flat`}
                style={{ fontSize: `17px`, width: `30px`, height: `30px`, borderRadius: 30, padding: 0, margin: 0 }}
                onClick={() => {
                  setReplacementValues((prevValue) => [...prevValue, { key: `${count}`, value: `stop_field` }]);
                  setCount((value) => value + 1);
                }}
              >
                +
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </RoutalDialog>
    );
  }
);
